"use client";

import { Box, Flex } from "@chakra-ui/react";
import type { GetTeacherResponse } from "@/openapi/generated";
import {
  usePresignedImageErrorRecovery,
  useTeacherPublicProfileImage,
} from "@/features/media/hooks";
import { buildTeacherPublicPath, appendFromListingQuery } from "@/lib/utils/publicPaths";
import {
  getTeacherPublicDisplayName,
  getTeacherPublicShortName,
} from "@/lib/utils/teacherPublic";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Card, Heading, Link, Text } from "@/components/ui";
import { ImageWithFallback } from "@/components/shared";
import { MdLocationOn } from "react-icons/md";

export interface TeacherListCardProps {
  teacher: GetTeacherResponse;
  imageUrl?: string | null;
}

export const TeacherListCard = ({ teacher, imageUrl }: TeacherListCardProps) => {
  const theme = useThemeTokens();
  const teacherId = Number(teacher.teacherId);

  const { url: profileImageUrl, refreshImage } = useTeacherPublicProfileImage(
    teacherId,
    { enabled: teacherId > 0 && imageUrl == null },
  );

  const resolvedImageUrl = imageUrl ?? profileImageUrl;
  const handleProfileImageError = usePresignedImageErrorRecovery(
    resolvedImageUrl,
    refreshImage,
  );
  const href = appendFromListingQuery(
    buildTeacherPublicPath({
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      teacherId,
    }),
    "teachers",
  );
  const { heading, subline } = getTeacherPublicDisplayName(teacher);
  const shortName = getTeacherPublicShortName(teacher);
  const bioPreview = teacher.biography?.trim();
  const locationNames = (teacher.workLocations ?? [])
    .map((loc) => loc.name?.trim())
    .filter(Boolean)
    .slice(0, 2);

  return (
    <Link href={href} display="block" h="100%" _hover={{ textDecoration: "none" }}>
      <Card
        p={4}
        h="100%"
        transition="all 0.2s ease"
        sx={{
          "@media (hover: hover)": {
            "&:hover": {
              borderColor: theme.accent.primaryBorder,
              boxShadow: theme.shadow.cardFeatured,
              transform: "translateY(-2px)",
            },
          },
        }}
      >
        <Flex align="flex-start" gap={4}>
          <Box
            flexShrink={0}
            w="72px"
            borderRadius="xl"
            overflow="hidden"
            aspectRatio="3 / 4"
            bg={theme.borderGlass.subtle}
            boxShadow="sm"
          >
            <ImageWithFallback
              src={resolvedImageUrl}
              alt={shortName}
              fallbackType="avatar"
              width="100%"
              height="100%"
              borderRadius="0"
              objectFit="cover"
              onImageError={handleProfileImageError}
            />
          </Box>

          <Flex direction="column" flex={1} minW={0} gap={1}>
            <Heading size="sm" fontWeight="600" lineHeight="1.25" noOfLines={2}>
              {heading}
            </Heading>
            {subline ? (
              <Text variant="secondary" fontSize="sm" noOfLines={1}>
                {subline}
              </Text>
            ) : null}
            {bioPreview ? (
              <Text variant="secondary" fontSize="sm" lineHeight="1.45" noOfLines={2}>
                {bioPreview}
              </Text>
            ) : null}
            {locationNames.length > 0 ? (
              <Flex align="center" gap={1.5} mt={0.5}>
                <Box color={theme.accent.primary} fontSize="14px" aria-hidden flexShrink={0}>
                  <MdLocationOn />
                </Box>
                <Text variant="tertiary" fontSize="xs" noOfLines={1}>
                  {locationNames.join(" · ")}
                </Text>
              </Flex>
            ) : null}
          </Flex>
        </Flex>
      </Card>
    </Link>
  );
};
